import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddBusiness,
  ContentCopy,
  PlayArrow,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import {
  useGetDemoOrgDefaultRosterQuery,
  usePreviewDemoOrgMutation,
  useProvisionDemoOrgMutation,
} from '../store/hooks';
import type { DemoOrgPreviewResponse, DemoOrgProvisionRequest } from '../types';
import {
  DemoOrgRosterEditor,
  type PersonaDraft,
} from '../components/demo-org-roster-editor';
import { DemoOrgProvisionProgress } from '../components/demo-org-provision-progress';
import {
  PROVISION_PHASES,
  useDemoOrgProvisionStream,
} from '../hooks/useDemoOrgProvisionStream';

const PASSWORD_MIN_LENGTH = 8;

function extractError(err: unknown): string {
  const data = (err as { data?: Record<string, unknown> })?.data;
  if (data) {
    const msg = data.message ?? data.detail ?? data.error;
    if (typeof msg === 'string') return msg;
  }
  return 'Request failed. Check the roster and try again.';
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function DemoOrgProvisioning() {
  const { enqueueSnackbar } = useSnackbar();

  const [orgName, setOrgName] = useState('');
  const [orgSlug, setOrgSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [personas, setPersonas] = useState<PersonaDraft[]>([]);

  const [previewResult, setPreviewResult] = useState<DemoOrgPreviewResponse | null>(
    null,
  );
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [jobId, setJobId] = useState<string | null>(null);

  const { data: defaultRoster } = useGetDemoOrgDefaultRosterQuery();
  const [preview, { isLoading: isPreviewing }] = usePreviewDemoOrgMutation();
  const [provision, { isLoading: isProvisioning }] = useProvisionDemoOrgMutation();
  const stream = useDemoOrgProvisionStream(jobId);

  // Prefill from the standard roster once (don't clobber operator edits on refetch).
  useEffect(() => {
    if (defaultRoster?.personas && personas.length === 0) {
      setPersonas(
        defaultRoster.personas.map((p) => ({
          rowId: crypto.randomUUID(),
          key: p.key,
          first_name: p.first_name,
          last_name: p.last_name,
          roles: p.roles,
          is_owner: p.is_owner ?? false,
          needs_cosign: p.needs_cosign ?? false,
          supervisor_key: p.supervisor_key ?? '',
        })),
      );
    }
  }, [defaultRoster, personas.length]);

  const isTerminal = stream.status === 'completed' || stream.status === 'failed';
  const isInFlight = isProvisioning || (!!jobId && !isTerminal);
  const formDisabled = isInFlight;

  const personaNameByKey = useMemo(() => {
    const map: Record<string, string> = {};
    personas.forEach((p) => {
      map[p.key] = `${p.first_name} ${p.last_name}`.trim();
    });
    return map;
  }, [personas]);

  const validate = (): string | null => {
    if (!orgName.trim()) return 'Organization name is required.';
    if (!orgSlug.trim()) return 'Organization slug is required.';
    if (password.length < PASSWORD_MIN_LENGTH)
      return `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`;
    if (personas.length === 0) return 'Add at least one persona.';
    const owners = personas.filter((p) => p.is_owner);
    if (owners.length !== 1) return 'Exactly one persona must be the owner.';
    const keys = personas.map((p) => p.key.trim());
    if (keys.some((k) => !k)) return 'Every persona needs a key.';
    if (new Set(keys).size !== keys.length) return 'Persona keys must be unique.';
    if (personas.some((p) => p.roles.length === 0))
      return 'Every persona needs at least one role.';
    const keySet = new Set(keys);
    if (personas.some((p) => p.supervisor_key && !keySet.has(p.supervisor_key)))
      return 'A supervisor reference points to an unknown persona key.';
    return null;
  };

  const buildRequest = (): DemoOrgProvisionRequest => ({
    org_name: orgName.trim(),
    org_slug: orgSlug.trim(),
    password,
    personas: personas.map((p) => ({
      key: p.key.trim(),
      first_name: p.first_name.trim(),
      last_name: p.last_name.trim(),
      roles: p.roles,
      is_owner: p.is_owner,
      needs_cosign: p.needs_cosign,
      ...(p.supervisor_key ? { supervisor_key: p.supervisor_key } : {}),
    })),
  });

  const handlePreview = async () => {
    const error = validate();
    if (error) {
      enqueueSnackbar(error, { variant: 'warning' });
      return;
    }
    setPreviewError(null);
    try {
      const result = await preview({
        demoOrgProvisionRequest: buildRequest(),
      }).unwrap();
      setPreviewResult(result);
    } catch (err) {
      setPreviewResult(null);
      setPreviewError(extractError(err));
    }
  };

  const handleProvision = async () => {
    const error = validate();
    if (error) {
      enqueueSnackbar(error, { variant: 'warning' });
      return;
    }
    try {
      const result = await provision({
        demoOrgProvisionRequest: buildRequest(),
      }).unwrap();
      setJobId(result.job_id);
      enqueueSnackbar('Provisioning started', { variant: 'info' });
    } catch (err) {
      enqueueSnackbar(extractError(err), { variant: 'error' });
    }
  };

  const handleStartOver = () => {
    setJobId(null);
    setPreviewResult(null);
    setPreviewError(null);
  };

  const copyPassword = () => {
    navigator.clipboard?.writeText(password);
    enqueueSnackbar('Password copied', { variant: 'success' });
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Provision Demo Org
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure a roster of personas (each a real Auth0 login) and provision a demo
        organization. Provisioning runs in the background and may take up to a minute.
      </Typography>

      {/* Result / status when a job is in flight or terminal */}
      {jobId && (
        <Card sx={{ mb: 3, borderColor: 'primary.main', borderWidth: 1, borderStyle: 'solid' }}>
          <CardContent>
            {!isTerminal && (
              <>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6">Provisioning…</Typography>
                  <Chip size="small" label={stream.status} />
                </Stack>
                <DemoOrgProvisionProgress
                  phases={PROVISION_PHASES}
                  currentPhase={stream.currentPhase}
                  completedPhases={stream.completedPhases}
                  detail={stream.detail}
                  status={stream.status}
                />
                <Typography variant="caption" color="text.secondary">
                  Job {jobId}
                </Typography>
              </>
            )}

            {stream.status === 'completed' && (
              <>
                <Typography variant="h6" color="success.main" gutterBottom>
                  Demo org provisioned
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Organization ID: <strong>{stream.result?.organization_id}</strong>
                </Typography>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Shared password (used by every persona; not shown again):{' '}
                  <strong>{password}</strong>
                  <Tooltip title="Copy password">
                    <IconButton size="small" aria-label="Copy shared password" onClick={copyPassword}>
                      <ContentCopy fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Alert>
                <Table size="small" aria-label="Provisioned personas">
                  <TableHead>
                    <TableRow>
                      <TableCell>Persona</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>User ID</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(stream.result?.provider_mapping ?? {}).map(
                      ([key, userId]) => (
                        <TableRow key={key}>
                          <TableCell>{key}</TableCell>
                          <TableCell>{personaNameByKey[key] ?? ''}</TableCell>
                          <TableCell>{userId}</TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
                <Box sx={{ mt: 2 }}>
                  <Button onClick={handleStartOver}>Provision another</Button>
                </Box>
              </>
            )}

            {stream.status === 'failed' && (
              <>
                <Alert severity="error" sx={{ mb: 2 }}>
                  Provisioning failed: {stream.error ?? 'Unknown error'}
                </Alert>
                <Button onClick={handleStartOver}>Start over</Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {!isTerminal && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack spacing={2}>
                <TextField
                  label="Organization name"
                  value={orgName}
                  onChange={(e) => {
                    const name = e.target.value;
                    setOrgName(name);
                    if (!slugEdited) setOrgSlug(slugify(name));
                  }}
                  disabled={formDisabled}
                  fullWidth
                />
                <TextField
                  label="Organization slug"
                  value={orgSlug}
                  onChange={(e) => {
                    setOrgSlug(e.target.value);
                    setSlugEdited(true);
                  }}
                  disabled={formDisabled}
                  helperText="Auto-filled from the name; edit if needed. Namespaces synthetic persona emails."
                  fullWidth
                />
                <TextField
                  label="Shared password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={formDisabled}
                  helperText={`At least ${PASSWORD_MIN_LENGTH} characters. You'll use this to log in as any persona.`}
                  fullWidth
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={
                              showPassword ? 'Hide password' : 'Show password'
                            }
                            onClick={() => setShowPassword((v) => !v)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Roster
              </Typography>
              <DemoOrgRosterEditor
                personas={personas}
                onChange={setPersonas}
                disabled={formDisabled}
              />
            </CardContent>
          </Card>

          {previewError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {previewError}
            </Alert>
          )}

          {previewResult && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Plan looks valid</Typography>
              <Typography variant="body2">
                {previewResult.persona_count} personas · owner:{' '}
                <strong>{previewResult.owner_key}</strong>
                {previewResult.cosign_pairs && previewResult.cosign_pairs.length > 0 && (
                  <>
                    {' '}
                    · cosign:{' '}
                    {previewResult.cosign_pairs
                      .map(
                        (c) =>
                          `${c.clinician_key}→${c.supervisor_key ?? 'any supervisor'}`,
                      )
                      .join(', ')}
                  </>
                )}
              </Typography>
            </Alert>
          )}

          <Divider sx={{ mb: 2 }} />
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={handlePreview}
              disabled={formDisabled || isPreviewing}
              startIcon={
                isPreviewing ? <CircularProgress size={16} /> : <PlayArrow />
              }
            >
              Preview (dry run)
            </Button>
            <Button
              variant="contained"
              onClick={handleProvision}
              disabled={formDisabled}
              startIcon={
                isProvisioning ? <CircularProgress size={16} /> : <AddBusiness />
              }
            >
              Provision
            </Button>
          </Stack>
        </>
      )}
    </Container>
  );
}
